import { useEffect, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import { Like, LikeFilled } from "../../components/svgCollection"
import PrivilegeTag, { PRIVILEGE_TAGS } from "../film/PrivilegeTag"
import FilmMetaAboveTitle from "../film/FilmMetaAboveTitle"
import TrailerModal from "../../components/TrailerModal"
import TrailerPlayer from "../../components/TrailerPlayer"
import ResponsiveFilmPoster, { filmPosterData } from "../../components/ResponsiveFilmPoster"
import { useRequest } from "../../hooks/useRequest"
import { userStore } from "../../store/userStore"
import { Movie } from "../../types/entities/movie"
import SessionString from "./SessionString"
import { useCinemaHref } from "../../hooks/useCinemaHref"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export default function MovieItem({
    item,
    index = 0,
    showSessionDates = false,
}: {
    item: Movie
    index?: number
    /** Группировать сеансы по датам (подборки) */
    showSessionDates?: boolean
}) {
    const isMobile = window.matchMedia("(max-width: 768px)").matches
    const { request } = useRequest()
    const { user } = userStore()
    const navigate = useNavigate()
    const href = useCinemaHref()
    const filmTo = href(`/afisha/film/${item.id}`)
    const filmState = {
        pageTitle: item.name,
        countries: item.countries,
        duration: item.duration,
    }
    const [isFavorite, setIsFavorite] = useState(item.favourites)
    const [toggling, setToggling] = useState(false)
    const [showTrailer, setShowTrailer] = useState(false)

    useEffect(() => {
        setIsFavorite(item.favourites)
    }, [item.favourites, item.id])

    useEffect(() => {
        setShowTrailer(false)
    }, [item.id])

    const toggleFavorite = async () => {
        if (!user) {
            navigate("/auth")
            return
        }
        if (toggling) return
        setToggling(true)
        try {
            if (isFavorite) {
                await request("DELETE", "user/favorite", { film_id: item.id })
            } else {
                await request("POST", "user/favorite", { film_id: item.id })
            }
            setIsFavorite(prev => !prev)
        } finally {
            setToggling(false)
        }
    }

    const activeTags = PRIVILEGE_TAGS.filter(t => item.privileges?.[t.key])
    const trailerSrc = item.trailer
    const closeTrailer = useCallback(() => setShowTrailer(false), [])

    const showPremiereBadge = Boolean(item.premiere && !item.buy && !item.sessions?.length)

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.07, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative flex flex-col gap-5 md:flex-row md:gap-8 rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-6 group">
            {/* Glow clipped to card shape */}
            <div className="absolute inset-0 rounded-[24px] overflow-hidden pointer-events-none">
                <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: "linear-gradient(135deg, rgba(52,155,230,0.08) 0%, transparent 60%)" }} />
            </div>

            {/* Poster / trailer */}
            <div className="relative shrink-0 self-start w-full overflow-hidden rounded-[16px] aspect-[650/348] md:h-[260px] md:w-auto md:aspect-[473/677]">
                {showTrailer && trailerSrc && isMobile ? (
                    <>
                        <TrailerPlayer
                            src={trailerSrc}
                            title={item.name}
                            className="absolute inset-0 h-full w-full rounded-[16px] border-0 bg-black"
                        />
                        <button
                            type="button"
                            onClick={closeTrailer}
                            aria-label="Закрыть трейлер"
                            className="absolute top-2 right-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full border border-white/25 bg-black/60 text-white/90 transition-colors hover:bg-black/80">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    </>
                ) : (
                    <>
                        <Link to={filmTo} state={filmState} className="relative block size-full">
                            <ResponsiveFilmPoster
                                film={item}
                                alt={item.name}
                                className="absolute inset-0 size-full rounded-[16px] object-cover"
                            />
                        </Link>
                        {trailerSrc && (
                            <motion.button
                                type="button"
                                aria-label="Смотреть трейлер"
                                whileHover={{ scale: 1.06 }}
                                whileTap={{ scale: 0.94 }}
                                onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setShowTrailer(true)
                                }}
                                className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-[16px] bg-black/0 transition-colors hover:bg-black/20">
                                <div
                                    className="flex size-12 items-center justify-center rounded-full border border-white bg-transparent"
                                    style={{ boxShadow: "0 0 24px rgba(52,155,230,0.35)" }}>
                                    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden>
                                        <path d="M1 1.5v13l11-6.5L1 1.5z" fill="white" stroke="white" strokeWidth="1" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </motion.button>
                        )}
                    </>
                )}
            </div>

            {typeof document !== "undefined" &&
                createPortal(
                    <AnimatePresence>
                        {showTrailer && trailerSrc && !isMobile && (
                            <TrailerModal key={item.id} title={item.name} src={trailerSrc} onClose={closeTrailer} />
                        )}
                    </AnimatePresence>,
                    document.body
                )}

            {/* Info */}
            <div className="flex w-full flex-col gap-5 min-w-0 md:gap-6">
                {/* Title row */}
                <div className="min-w-0">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <FilmMetaAboveTitle
                            className=""
                            countries={item.countries}
                            duration={item.duration}
                            premiere={showPremiereBadge ? undefined : item.premiere}
                        />
                        <div className="ml-auto flex shrink-0 items-center gap-2">
                            <div
                                className="flex size-9 items-center justify-center rounded-full border border-white/20 bg-white/5 text-[11px] text-white/80"
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                {item.age}
                            </div>
                            <motion.button
                                type="button"
                                disabled={toggling}
                                onClick={toggleFavorite}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.88 }}
                                className={`flex items-center justify-center size-9 rounded-full border transition-all ${
                                    isFavorite
                                        ? "border-accent bg-accent/20"
                                        : "border-white/20 bg-white/5 opacity-60 hover:opacity-100"
                                } ${toggling ? "cursor-not-allowed" : "cursor-pointer"}`}
                                style={{
                                    boxShadow: isFavorite ? "0 0 14px rgba(228,32,107,0.4)" : "none",
                                }}>
                                {isFavorite ? <LikeFilled /> : <Like />}
                            </motion.button>
                        </div>
                    </div>

                    <Link to={filmTo} state={filmState} className="block min-w-0">
                        <h3
                            className="text-[24px] text-white leading-tight transition-colors hover:text-[#349be6] md:text-[28px]"
                            style={{
                                fontFamily: DISPLAY,
                                fontWeight: 800,
                                letterSpacing: "-0.02em",
                            }}>
                            {item.name}
                        </h3>
                    </Link>
                </div>

                {/* Privilege tags */}
                {activeTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {activeTags.map(t => (
                            <PrivilegeTag key={t.key} label={t.label} color={t.color} />
                        ))}
                    </div>
                )}

                {/* Session times / soon buy */}
                {item.buy ? (
                    <Link
                        to={filmTo}
                        state={filmState}
                        className="self-start rounded-full px-5 py-2.5 text-[13px] text-white transition-opacity hover:opacity-90"
                        style={{ background: "#e4206b", fontFamily: MONO, fontWeight: 700 }}>
                        Купить билет
                    </Link>
                ) : item.sessions?.length > 0 ? (
                        <SessionString
                            sessions={item.sessions}
                            showDates={showSessionDates}
                            movieData={{
                                privileges: item.privileges ?? {
                                    pushkin_card: false,
                                    discount: false,
                                    child_place: false,
                                    invitational: false,
                                },
                                ...filmPosterData(item),
                            }}
                        />
                    ) : showPremiereBadge ? (
                        <div
                            className="self-start rounded-full border border-[#349be6]/35 bg-[#349be6]/10 px-5 py-2.5 text-[13px] text-[#349be6]"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            Премьера · {item.premiere}
                        </div>
                    ) : null}
            </div>
        </motion.div>
    )
}
